// Companies — the go-pim company list, wired to GET /v1/companies.
//
// The company is go-pim's first-class tenant and carries the issuer identity.
// This is the list + "new company" modal; the full issuer / taxation / template
// form lives on CompanyDetail (/companies/:id). Modeled on strata's Merchants.
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { Modal, Pill } from "@/components/UI";
import { formatDateTime } from "@/utils/format";
import { downloadCsv } from "@/utils/csv";
import { apiErrorMessage } from "@/utils/apiError";
import { useCompanies } from "@/queries/useCompanies";
import {
  createCompany,
  companyWarnings,
  issuingStatus,
  isValidBusinessProfileId,
  businessProfileIdHint,
  MAX_BUSINESS_PROFILE_ID_LEN,
  DOCUMENT_MODES,
  DOCUMENT_MODE_LABELS,
  type CompanyWritePayload,
  type DocumentMode,
  type SyncState,
} from "@/api/companies";

// Where a company came from, said plainly. The list previously showed only
// "Linked"/"—", which collapsed three genuinely different states into one and
// gave an operator no way to tell a widget-owned row from one they typed.
const SYNC_LABELS: Record<SyncState, { text: string; tone: string }> = {
  synced: { text: "From widget", tone: "var(--pos)" },
  local: { text: "Local", tone: "var(--muted)" },
  absent: { text: "Gone from widget", tone: "var(--neg)" },
};

// ─── New company modal ────────────────────────────────────────────────────────

function NewCompanyModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState("");
  const [businessProfileId, setBusinessProfileId] = useState("");
  const [documentMode, setDocumentMode] = useState<DocumentMode>("none");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setDisplayName("");
    setBusinessProfileId("");
    setDocumentMode("none");
    setError(null);
    setSaving(false);
  }

  function close() {
    reset();
    onClose();
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    // ── Blocking guardrails ──────────────────────────────────────────────
    const name = displayName.trim();
    if (name === "") {
      setError("Display name can't be empty.");
      return;
    }
    const bp = businessProfileId.trim();
    if (bp !== "" && !isValidBusinessProfileId(bp)) {
      setError(
        "Collection profile can't contain spaces and must be under 200 characters. Paste the id exactly as widget shows it, or leave it blank.",
      );
      return;
    }
    setSaving(true);
    setError(null);
    // Create with sane defaults; the operator fills the issuer/taxation/template
    // and delivery detail on the company page afterwards.
    const payload: CompanyWritePayload = {
      display_name: name,
      widget_business_profile_id: bp || null,
      // Uncategorised at creation; set on the company page once the operator
      // knows which MCC widget should present for this merchant.
      mcc: "",
      issuer: {},
      document_mode: documentMode,
      taxation: "none",
      default_gst_rate_bps: 0,
      // Empty delivery identity = fall back to the platform defaults, which is
      // where every company starts and a perfectly valid resting state. Sent
      // explicitly rather than omitted so the create payload is the same shape
      // the edit form round-trips.
      public_base_url: "",
      email_domain: "",
      notification_brand: "",
      from_name: "",
      from_address: "",
      reply_to: "",
      invoice_template: "",
      receipt_template: "",
    };
    try {
      const c = await createCompany(payload);
      await qc.invalidateQueries({ queryKey: ["companies"] });
      reset();
      onClose();
      navigate(`/companies/${c.id}`);
    } catch (err) {
      setError(apiErrorMessage(err, "Could not create the company. Try again."));
      setSaving(false);
    }
  }

  const canSubmit = !saving && displayName.trim() !== "";
  // A shape nudge, never a gate — the id belongs to widget, not to this panel.
  const bpHint = businessProfileIdHint(businessProfileId);

  // Live "is this company going to issue?" preview + soft foot-gun notices,
  // so the operator internalises the model before they even hit save.
  const status = issuingStatus({
    widget_business_profile_id: businessProfileId.trim() || null,
    document_mode: documentMode,
  });
  const warnings = companyWarnings({
    document_mode: documentMode,
    invoice_template: "",
    receipt_template: "",
    issuer: {},
  });

  return (
    <Modal
      open={open}
      onClose={close}
      busy={saving}
      title="New company"
      foot={
        <>
          <button className="btn ghost" type="button" onClick={close} disabled={saving}>
            Cancel
          </button>
          <div className="flex-1" />
          <button
            className="btn accent"
            type="submit"
            form="new-company-form"
            disabled={!canSubmit}
          >
            {saving ? "Creating…" : "Create company"}
          </button>
        </>
      }
    >
      <form id="new-company-form" onSubmit={handleSubmit} className="stack-16">
        <div className="dim" style={{ fontSize: 12 }}>
          A company issues documents for the payments of one widget collection
          profile. Merchants that exist in widget arrive here on their own once
          the entity sync has run — create one by hand only if it does not.
        </div>
        <div className="field">
          <label className="label" htmlFor="nc-name">
            Display name
          </label>
          <input
            id="nc-name"
            className="input"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            autoFocus
          />
        </div>
        <div className="field">
          <label className="label" htmlFor="nc-bp">
            Collection profile <span className="opt">optional</span>
          </label>
          <input
            id="nc-bp"
            className="input mono"
            type="text"
            value={businessProfileId}
            onChange={(e) => setBusinessProfileId(e.target.value)}
            placeholder="bp_3fa85f64-5717-4562-b3fc-2c963f66afa6"
            maxLength={MAX_BUSINESS_PROFILE_ID_LEN}
            spellCheck={false}
          />
          <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
            The widget business profile whose payments this company issues
            documents for. Paste it <strong>exactly</strong> as widget shows it,
            prefix included. Until this is set, the company issues nothing.
          </div>
          {bpHint && (
            <div
              className="row gap-6"
              style={{ color: "var(--warn)", fontSize: 11, marginTop: 4 }}
            >
              <I.AlertCircle size={12} />
              <span>{bpHint}</span>
            </div>
          )}
        </div>
        <div className="field">
          <label className="label" htmlFor="nc-mode">
            Documents to issue
          </label>
          <select
            id="nc-mode"
            className="input"
            value={documentMode}
            onChange={(e) => setDocumentMode(e.target.value as DocumentMode)}
          >
            {DOCUMENT_MODES.map((m) => (
              <option key={m} value={m}>
                {DOCUMENT_MODE_LABELS[m]}
              </option>
            ))}
          </select>
        </div>
        <div className="row gap-12" style={{ alignItems: "center" }}>
          <span className="dim" style={{ fontSize: 12 }}>
            Status
          </span>
          <Pill tone={status.tone}>{status.label}</Pill>
        </div>
        {warnings.length > 0 && (
          <div className="stack-2">
            {warnings.map((w) => (
              <div
                key={w}
                className="row gap-6"
                style={{ color: "var(--warn)", fontSize: 12, alignItems: "center" }}
              >
                <I.AlertCircle size={12} />
                <span>{w}</span>
              </div>
            ))}
          </div>
        )}
        {error && <div style={{ color: "var(--neg)", fontSize: 12 }}>{error}</div>}
      </form>
    </Modal>
  );
}

export function Companies() {
  const navigate = useNavigate();
  const { data: companies = [], isLoading, isError } = useCompanies();
  const [newOpen, setNewOpen] = useState(false);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">Console · go-pim</div>
          <h1>Companies</h1>
          <div className="sub">
            {isLoading
              ? "Loading…"
              : `${companies.length} compan${companies.length === 1 ? "y" : "ies"}`}
          </div>
        </div>
        <div className="actions">
          <button
            className="btn"
            type="button"
            onClick={() =>
              downloadCsv(
                "companies.csv",
                companies.map((c) => ({
                  id: c.id,
                  display_name: c.display_name,
                  issuing: issuingStatus(c).label,
                  origin: c.sync_state,
                  widget_status: c.widget_status,
                  widget_synced_at: c.widget_synced_at,
                  widget_business_profile_id: c.widget_business_profile_id ?? "",
                  document_mode: c.document_mode,
                  taxation: c.taxation,
                  created_at: c.created_at,
                })),
              )
            }
          >
            <I.Download size={12} /> Export
          </button>
          <button className="btn accent" type="button" onClick={() => setNewOpen(true)}>
            <I.Plus size={12} /> New company
          </button>
        </div>
      </div>

      <NewCompanyModal open={newOpen} onClose={() => setNewOpen(false)} />

      <div className="card">
        {isLoading ? (
          <div className="empty">
            <div className="dim" style={{ fontSize: 13 }}>
              Loading companies…
            </div>
          </div>
        ) : isError ? (
          <div className="empty">
            <div className="icon">
              <I.Building size={16} />
            </div>
            <h3>Couldn't load companies</h3>
            <p>The request to the go-pim admin API failed. Try again shortly.</p>
          </div>
        ) : companies.length === 0 ? (
          <div className="empty">
            <div className="icon">
              <I.Building size={16} />
            </div>
            <h3>No companies yet</h3>
            <p>
              A company issues documents for the payments of one widget
              collection profile. Create your first to start issuing.
            </p>
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Company</th>
                <th>Issuing?</th>
                <th>Origin</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((c) => {
                const status = issuingStatus(c);
                const linked =
                  c.widget_business_profile_id != null &&
                  c.widget_business_profile_id.trim() !== "";
                const origin = SYNC_LABELS[c.sync_state] ?? SYNC_LABELS.local;
                return (
                  <tr
                    key={c.id}
                    className="clickable"
                    onClick={() => navigate(`/companies/${c.id}`)}
                  >
                    <td>
                      <div className="stack-2">
                        <div className="cell-name">{c.display_name}</div>
                        <div className="cell-id mono">{c.id}</div>
                      </div>
                    </td>
                    <td>
                      <Pill tone={status.tone}>{status.label}</Pill>
                    </td>
                    <td>
                      <div className="stack-2">
                        <span style={{ color: origin.tone, fontSize: 12 }}>
                          {origin.text}
                        </span>
                        {linked && (
                          <span className="cell-id mono">
                            {c.widget_business_profile_id}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="dim">{formatDateTime(c.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
